import React, { memo, useCallback, useEffect, useState } from "react";
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Dimensions,
  Image,
  Linking,
  ScrollView,
  TextInput,
  Platform,
} from "react-native";
import { getFileFromS3, pushFileToS3 } from "../../../services/s3BucketService";
import {
  addDeliveryAttachment,
  getDeliveryAttachment,
  updateDeliveryAttachment,
} from "../../../services/deliveryService";
import FilePreview from "../../Operations/utils/FilePreview";
import { ReadOnlyProps } from "../../../utils/templates";
import { BlurView } from "expo-blur";
import { IconButton, Text } from "react-native-paper";
import Spinner from "../../../components/shared/Spinner";
import { useToast } from "react-native-toast-notifications";
import * as ImagePicker from "expo-image-picker";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { Ionicons } from "@expo/vector-icons";
import { DeviceOrientation } from "../../../components/context/utils/DeviceOrientation";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/useTranslation";
import { updateDeliveryAttachmentFlag } from "../../../redux/reducers/deliveryReducer";

interface UploadAttachmentDeliveryContentProps {
  selectedItem: any;
  setModalVisible: (visible: boolean) => void;
}

const UploadAttachmentDeliveryContent: React.FC<UploadAttachmentDeliveryContentProps> =
  memo(({ selectedItem, setModalVisible }) => {
    const { colors } = useAppTheme();
    const styles = createStyles(colors);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [deliveryAttachment, setDeliveryAttachment] = useState<any>(null);
    const [files, setFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const toast = useToast();
    const [docNum] = useState(selectedItem?.DocNum);
    const [remarks, setRemarks] = useState<string>("");
    const [previewUri, setPreviewUri] = useState<string | null>(null);
    const { userInfo } = useSelector((state: any) => state.configurations);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // ---- Camera capture (web) — always single-shot ----
    const captureFromCameraWeb = (category: "dn" | "chep" | "additional") => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.setAttribute("capture", "environment");

      // Keep it in the DOM but out of sight — required for the change
      // event to fire reliably on the FIRST capture (iOS/iPadOS Safari)
      input.style.position = "fixed";
      input.style.left = "-9999px";
      input.style.top = "0";

      const cleanup = () => {
        if (input.parentNode) input.parentNode.removeChild(input);
      };

      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) {
          cleanup();
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          setFiles((prev) => [
            ...prev,
            {
              id: Math.random().toString(36).substring(2, 15),
              fileName: file.name || `camera_${Date.now()}.jpg`,
              fileUri: reader.result as string,
              fileType: file.type || "image/jpeg",
              isNew: true,
              category,
            },
          ]);
          cleanup();
        };
        reader.onerror = () => {
          toast.show(
            t("couldNotReadImage") || "Could not read captured image",
            { type: "danger" },
          );
          cleanup();
        };
        reader.readAsDataURL(file);
      };

      document.body.appendChild(input);
      input.click();
    };

    // ---- Gallery picker (web) — multi-select ----
    const pickMultipleFromGalleryWeb = (
      category: "dn" | "chep" | "additional",
    ) => {
      const input = document.createElement("input");
      input.type = "file";
      input.setAttribute("accept", "image/*");
      // Set BOTH the property and the attribute — some browsers only
      // respect one or the other depending on when the input was created.
      input.multiple = true;
      input.setAttribute("multiple", "multiple");
      // Explicitly no "capture" attr here -> opens file browser/gallery, not camera

      input.style.position = "fixed";
      input.style.left = "-9999px";
      input.style.top = "0";

      const cleanup = () => {
        if (input.parentNode) input.parentNode.removeChild(input);
      };

      input.onchange = () => {
        const selectedFiles = input.files ? Array.from(input.files) : [];
        if (selectedFiles.length === 0) {
          cleanup();
          return;
        }

        let processed = 0;
        selectedFiles.forEach((file, idx) => {
          const reader = new FileReader();
          reader.onload = () => {
            setFiles((prev) => [
              ...prev,
              {
                id: Math.random().toString(36).substring(2, 15),
                fileName: file.name || `gallery_${Date.now()}_${idx}.jpg`,
                fileUri: reader.result as string,
                fileType: file.type || "image/jpeg",
                isNew: true,
                category,
              },
            ]);
            processed += 1;
            if (processed === selectedFiles.length) cleanup();
          };
          reader.onerror = () => {
            toast.show(
              t("couldNotReadImage") || "Could not read selected image",
              { type: "danger" },
            );
            processed += 1;
            if (processed === selectedFiles.length) cleanup();
          };
          reader.readAsDataURL(file);
        });
      };

      document.body.appendChild(input);
      input.click();
    };

    // ---- Camera capture (native) — always single-shot ----
    const captureFromCameraNative = async (
      category: "dn" | "chep" | "additional",
    ) => {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== "granted") {
        toast.show(
          t("cameraPermissionRequired") || "Camera permission required",
          { type: "danger" },
        );
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.3,
        base64: true,
        exif: false,
      });

      if (!result.canceled) {
        const asset = result.assets[0];
        setFiles((prev) => [
          ...prev,
          {
            id: Math.random().toString(36).substring(2, 15),
            fileName:
              asset.fileName ||
              `camera_${Date.now()}.${asset.uri.split(".").pop()}`,
            fileUri: asset.uri,
            fileType: asset.mimeType || "image/jpeg",
            isNew: true,
            category,
          },
        ]);
      }
    };

    // ---- Gallery picker (native) — multi-select ----
    const pickMultipleFromGalleryNative = async (
      category: "dn" | "chep" | "additional",
    ) => {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsMultipleSelection: true,
        quality: 0.3,
        base64: true,
        exif: false,
      });

      if (!result.canceled) {
        const newFiles = result.assets.map((asset) => ({
          id: Math.random().toString(36).substring(2, 15),
          fileName:
            asset.fileName ||
            `file_${Date.now()}.${asset.uri.split(".").pop()}`,
          fileUri: asset.uri,
          fileType: asset.mimeType || "image/jpeg",
          isNew: true,
          category,
        }));
        setFiles((prev) => [...prev, ...newFiles]);
      }
    };

    useEffect(() => {
      let isMounted = true;

      const fetchData = async () => {
        try {
          setLoading(true);
          const response = await getDeliveryAttachment(docNum);
          if (response?.data?.status && isMounted) {
            setDeliveryAttachment(response.data.data);
            const attachment = response.data.data.attachment;
            if (attachment) {
              const fileNames = attachment.split(",");
              const fetchedFiles: any[] = [];
              for (const name of fileNames) {
                if (!name.trim()) continue;
                const trimmedName = name.trim();
                const fileData = await getFileFromS3(
                  trimmedName,
                  `pod/${docNum}`,
                );
                if (fileData?.imageUrl && isMounted) {
                  let category: "dn" | "chep" | "additional" = "dn";
                  if (trimmedName.toLowerCase().startsWith("chep_")) {
                    category = "chep";
                  } else if (
                    trimmedName.toLowerCase().startsWith("additional_")
                  ) {
                    category = "additional";
                  }
                  fetchedFiles.push({
                    id: Math.random().toString(36).substring(2, 15),
                    fileName: trimmedName,
                    fileUri: fileData.imageUrl,
                    fileType: trimmedName.substring(
                      trimmedName.lastIndexOf(".") + 1,
                    ),
                    isNew: false,
                    category,
                  });
                }
              }
              setFiles(fetchedFiles);
            }
            if (response.data.data?.remarks && isMounted) {
              setRemarks(response.data.data.remarks);
            }
          }
        } catch (error) {
          console.error("Error fetching delivery attachment:", error);
        } finally {
          if (isMounted) setLoading(false);
        }
      };

      if (isMounted && docNum) {
        fetchData();
      }

      return () => {
        isMounted = false;
      };
    }, [docNum]);

    const handleSubmit = useCallback(async () => {
      try {
        setLoading(true);
        setIsUploading(true);

        const uploadedNames: string[] = [];

        for (const fileObj of files) {
          if (!fileObj.isNew) {
            // Already uploaded on S3, keep its existing key
            uploadedNames.push(fileObj.fileName);
          } else {
            // New local file, needs to be pushed to S3
            const uniqueId = new Date().getTime();
            const cleanName = fileObj.fileName.replace(/[\s,]+/g, "_");
            const prefix =
              fileObj.category === "chep"
                ? "chep_"
                : fileObj.category === "additional"
                  ? "additional_"
                  : "dn_";
            const uniqueFileName = `${prefix}${uniqueId}_${cleanName}`;

            const s3UploadResponse = await pushFileToS3(
              "POST",
              fileObj.fileUri,
              uniqueFileName,
              fileObj.fileType,
              // "delivery",
              `pod/${docNum}`,
            );

            if (s3UploadResponse) {
              uploadedNames.push(uniqueFileName);
            } else {
              throw new Error(`Failed to upload file: ${fileObj.fileName}`);
            }
          }
        }

        const commaSeparatedNames =
          uploadedNames.length > 0 ? uploadedNames.join(",") : " ";

        if (!deliveryAttachment) {
          await addDeliveryAttachment(
            selectedItem?.DocNum,
            commaSeparatedNames,
            remarks,
            selectedItem?.SOOperator,
          );
        } else {
          await updateDeliveryAttachment(
            selectedItem?.DocNum,
            commaSeparatedNames,
            remarks,
            selectedItem?.SOOperator,
          );
        }

        const now = new Date().toISOString();
        // Patch the matching row's attachment flag directly in deliveryList
        // rather than keeping a separate session-storage "recent uploads"
        // list. Any screen reading deliveryList sees the update immediately.
        dispatch(
          updateDeliveryAttachmentFlag({
            docNum: selectedItem?.DocNum,
            hasAdditionalAttachment: uploadedNames.length > 0,
            files: uploadedNames,
            remarks,
            uploadedBy: userInfo?.first_name
              ? `${userInfo.first_name} ${userInfo.last_name || ""}`.trim()
              : "Unknown",
            updatedAt: now,
          }),
        );

        toast.show(t("submissionSuccessful") || "Submission Successful", {
          type: "success",
        });
        setModalVisible(false);
      } catch (error) {
        console.error("Error submitting data: ", error);
        toast.show(t("submissionFailed") || "Submission Failed", {
          type: "danger",
        });
      } finally {
        setIsUploading(false);
        setLoading(false);
      }
    }, [
      files,
      deliveryAttachment,
      selectedItem,
      remarks,
      setModalVisible,
      toast,
      dispatch,
      userInfo,
      docNum,
    ]);

    const { isMediumDevice, isSmallDevice, isMobileDevice } =
      DeviceOrientation();
    const isMobileLayout = isSmallDevice || isMediumDevice || isMobileDevice;

    const selectImage = async (
      category: "dn" | "chep" | "additional",
      source: "camera" | "gallery",
    ) => {
      if (Platform.OS === "web") {
        source === "camera"
          ? captureFromCameraWeb(category)
          : pickMultipleFromGalleryWeb(category);
        return;
      }

      source === "camera"
        ? await captureFromCameraNative(category)
        : await pickMultipleFromGalleryNative(category);
    };

    const isImage = (fileType: string) => {
      if (!fileType) return false;
      const type = fileType.toLowerCase();
      return (
        type.includes("image") ||
        type.includes("jpg") ||
        type.includes("jpeg") ||
        type.includes("png") ||
        type.includes("gif") ||
        type.includes("webp")
      );
    };

    const getDisplayName = (fileName: string) => {
      return fileName.replace(/^(dn_|chep_|additional_)?\d+_/, "");
    };

    const dnFiles = files.filter(
      (f) => f.category === "dn" || f.category === "chep" || !f.category,
    );
    const additionalFiles = files.filter((f) => f.category === "additional");

    return (
      <View style={styles.container}>
        {loading ? (
          <View style={styles.loaderContainer}>
            <Spinner
              size="small"
              color={colors.primary}
              styles={{ alignSelf: "center" }}
            />
          </View>
        ) : (
          <>
            <Modal
              visible={!!previewUri}
              transparent
              animationType="fade"
              onRequestClose={() => setPreviewUri(null)}
            >
              <TouchableOpacity
                style={styles.previewOverlay}
                activeOpacity={1}
                onPress={() => setPreviewUri(null)}
              >
                {previewUri && (
                  <Image
                    source={{ uri: previewUri }}
                    style={styles.previewImage}
                    resizeMode="contain"
                  />
                )}
                <IconButton
                  icon="close"
                  color="#FFF"
                  size={28}
                  style={styles.previewClose}
                  onPress={() => setPreviewUri(null)}
                />
              </TouchableOpacity>
            </Modal>
            <ScrollView
              contentContainerStyle={styles.contentContainer}
              showsVerticalScrollIndicator={false}
              style={{ flex: 1 }}
            >
              {/* Header */}
              <View style={styles.header}>
                <Text style={styles.headerTitle}>
                  {t("attachmentManager") || "Attachment Manager"}
                </Text>
                <Text style={styles.headerSubtitle}>
                  {t("documentNo") || "Document No"}: #{docNum}
                </Text>
              </View>

              {/* POD & CHEP Upload Section */}
              <Text style={styles.sectionTitle}>
                {t("podChepDoc") || "POD & CHEP Doc"}
              </Text>

              {/* POD & CHEP Files List */}
              {dnFiles.length > 0 && (
                <View style={styles.filesListContainer}>
                  {dnFiles.map((fileObj, index) => (
                    <View key={fileObj.id} style={styles.previewCard}>
                      <View style={styles.previewContent}>
                        {isImage(fileObj.fileType) ? (
                          <Image
                            source={{ uri: fileObj.fileUri }}
                            style={styles.thumbnail}
                          />
                        ) : (
                          <View style={styles.docIconContainer}>
                            <Ionicons
                              name="document-text"
                              size={32}
                              color={colors.primary}
                            />
                          </View>
                        )}
                        <View style={styles.fileDetails}>
                          <Text
                            style={styles.fileName}
                            numberOfLines={1}
                            ellipsizeMode="middle"
                          >
                            {getDisplayName(fileObj.fileName)}
                          </Text>
                          <Text style={styles.fileType}>
                            {fileObj.fileType.toUpperCase()}
                            {fileObj.isNew && " (NEW)"}
                          </Text>
                        </View>
                      </View>

                      {/* Actions row inside the card */}
                      <View style={styles.cardActions}>
                        <TouchableOpacity
                          style={[styles.cardBtn, styles.viewBtn]}
                          onPress={() => setPreviewUri(fileObj.fileUri)}
                        >
                          <Ionicons
                            name="eye-outline"
                            size={16}
                            color={colors.primary}
                            style={{ marginRight: 6 }}
                          />
                          <Text style={styles.viewBtnText}>
                            {t("viewFile") || "View File"}
                          </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                          style={[styles.cardBtn, styles.removeBtn]}
                          onPress={() => {
                            setFiles((prev) =>
                              prev.filter((f) => f.id !== fileObj.id),
                            );
                          }}
                        >
                          <Ionicons
                            name="trash-outline"
                            size={16}
                            color={colors.danger}
                            style={{ marginRight: 6 }}
                          />
                          <Text style={styles.removeBtnText}>
                            {t("remove") || "Remove"}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {/* Upload controls for POD & CHEP */}
              {isMobileLayout ? (
                <View style={{ flexDirection: "row", marginBottom: 16 }}>
                  <TouchableOpacity
                    onPress={() => selectImage("dn", "camera")}
                    style={[
                      styles.uploadZone,
                      { flex: 1, marginRight: 8, marginBottom: 0 },
                    ]}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="camera-outline"
                      size={20}
                      color={colors.primary}
                      style={{ marginRight: 8 }}
                    />
                    <Text style={styles.uploadZoneTitle}>
                      {t("takePhoto") || "Take Photo"}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => selectImage("dn", "gallery")}
                    style={[styles.uploadZone, { flex: 1, marginBottom: 0 }]}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="images-outline"
                      size={20}
                      color={colors.primary}
                      style={{ marginRight: 8 }}
                    />
                    <Text style={styles.uploadZoneTitle}>
                      {t("chooseFromGallery") || "Choose from Gallery"}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity
                  onPress={() => selectImage("dn", "gallery")}
                  style={styles.uploadZone}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="cloud-upload-outline"
                    size={20}
                    color={colors.primary}
                    style={{ marginRight: 8 }}
                  />
                  <Text style={styles.uploadZoneTitle}>
                    {t("uploadPodChep") || "Select and Upload POD / CHEP File"}
                  </Text>
                </TouchableOpacity>
              )}

              {/* Additional Image Upload Section */}
              <Text style={[styles.sectionTitle, { marginTop: 12 }]}>
                {t("additionalImage") || "Additional Image"}
              </Text>

              {/* Additional Files List */}
              {additionalFiles.length > 0 && (
                <View style={styles.filesListContainer}>
                  {additionalFiles.map((fileObj, index) => (
                    <View key={fileObj.id} style={styles.previewCard}>
                      <View style={styles.previewContent}>
                        {isImage(fileObj.fileType) ? (
                          <Image
                            source={{ uri: fileObj.fileUri }}
                            style={styles.thumbnail}
                          />
                        ) : (
                          <View
                            style={[
                              styles.docIconContainer,
                              styles.additionalDocIconContainer,
                            ]}
                          >
                            <Ionicons
                              name="document-text"
                              size={32}
                              color={colors.primary}
                            />
                          </View>
                        )}
                        <View style={styles.fileDetails}>
                          <Text
                            style={styles.fileName}
                            numberOfLines={1}
                            ellipsizeMode="middle"
                          >
                            {getDisplayName(fileObj.fileName)}
                          </Text>
                          <Text style={styles.fileType}>
                            {fileObj.fileType.toUpperCase()}
                            {fileObj.isNew && " (NEW)"}
                          </Text>
                        </View>
                      </View>

                      {/* Actions row inside the card */}
                      <View style={styles.cardActions}>
                        <TouchableOpacity
                          style={[styles.cardBtn, styles.viewBtn]}
                          onPress={() => setPreviewUri(fileObj.fileUri)}
                        >
                          <Ionicons
                            name="eye-outline"
                            size={16}
                            color={colors.primary}
                            style={{ marginRight: 6 }}
                          />
                          <Text style={styles.viewBtnText}>
                            {t("viewFile") || "View File"}
                          </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                          style={[styles.cardBtn, styles.removeBtn]}
                          onPress={() => {
                            setFiles((prev) =>
                              prev.filter((f) => f.id !== fileObj.id),
                            );
                          }}
                        >
                          <Ionicons
                            name="trash-outline"
                            size={16}
                            color={colors.danger}
                            style={{ marginRight: 6 }}
                          />
                          <Text style={styles.removeBtnText}>
                            {t("remove") || "Remove"}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {/* Upload controls for Additional */}
              {isMobileLayout ? (
                <View style={{ flexDirection: "row", marginBottom: 16 }}>
                  <TouchableOpacity
                    onPress={() => selectImage("additional", "camera")}
                    style={[
                      styles.uploadZone,
                      styles.additionalUploadZone,
                      { flex: 1, marginRight: 8, marginBottom: 0 },
                    ]}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="camera-outline"
                      size={20}
                      color={colors.primary}
                      style={{ marginRight: 8 }}
                    />
                    <Text
                      style={[
                        styles.uploadZoneTitle,
                        styles.additionalUploadZoneTitle,
                      ]}
                    >
                      {t("takePhoto") || "Take Photo"}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => selectImage("additional", "gallery")}
                    style={[
                      styles.uploadZone,
                      styles.additionalUploadZone,
                      { flex: 1, marginBottom: 0 },
                    ]}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="images-outline"
                      size={20}
                      color={colors.primary}
                      style={{ marginRight: 8 }}
                    />
                    <Text
                      style={[
                        styles.uploadZoneTitle,
                        styles.additionalUploadZoneTitle,
                      ]}
                    >
                      {t("chooseFromGallery") || "Choose from Gallery"}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity
                  onPress={() => selectImage("additional", "gallery")}
                  style={[styles.uploadZone, styles.additionalUploadZone]}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="cloud-upload-outline"
                    size={20}
                    color={colors.primary}
                    style={{ marginRight: 8 }}
                  />
                  <Text
                    style={[
                      styles.uploadZoneTitle,
                      styles.additionalUploadZoneTitle,
                    ]}
                  >
                    {t("uploadAdditionalImage") ||
                      "Select and Upload Additional Image"}
                  </Text>
                </TouchableOpacity>
              )}

              {/* Remarks */}
              <Text style={styles.remarksLabel}>
                {t("remarksDescription") || "Remarks / Description"}
              </Text>
              <TextInput
                style={styles.textArea}
                placeholder={
                  t("provideAdditionalDetails") ||
                  "Provide additional details or notes here..."
                }
                placeholderTextColor="#94A3B8"
                value={remarks}
                onChangeText={setRemarks}
                multiline={true}
                numberOfLines={4}
              />
            </ScrollView>
            <View style={styles.footer}>
              <TouchableOpacity
                style={[
                  styles.submitButton,
                  isUploading && styles.submitButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={isUploading}
              >
                {isUploading ? (
                  <Spinner size="small" color={colors.surface} />
                ) : (
                  <Text style={styles.submitText}>
                    {t("submit") || "Submit"}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    );
  });

const ModalContainer: React.FC<ReadOnlyProps<Props>> = memo(
  ({ childern, modalVisible, setModalVisible }) => {
    const Children = childern;
    const { colors } = useAppTheme();
    const styles = createStyles(colors);

    return (
      <View>
        <Modal
          animationType="slide"
          transparent={true}
          visible={modalVisible}
          onRequestClose={() => setModalVisible(!modalVisible)}
        >
          <BlurView intensity={10} style={styles.centeredView}>
            <View
              style={[
                styles.modalView,
                {
                  width: "90%",
                  maxWidth: 680,
                  minHeight: 480,
                  maxHeight: "90%",
                },
              ]}
            >
              {modalVisible && Children && <Children />}
              <IconButton
                style={styles.button}
                icon="close"
                color={colors.textSecondary}
                size={22}
                onPress={() => setModalVisible(!modalVisible)}
              />
            </View>
          </BlurView>
        </Modal>
      </View>
    );
  },
);

interface UploadAttachmentDeliveryModalProps {
  selectedItem: any;
  deliveryAttachmentModalVisible: boolean;
  setDeliveryAttachmentModalVisible: (visible: boolean) => void;
}

export const UploadAttachmentDeliveryModal: React.FC<UploadAttachmentDeliveryModalProps> =
  memo(
    ({
      selectedItem,
      deliveryAttachmentModalVisible,
      setDeliveryAttachmentModalVisible,
    }) => {
      return (
        <ModalContainer
          modalVisible={deliveryAttachmentModalVisible}
          setModalVisible={setDeliveryAttachmentModalVisible}
          childern={() => (
            <UploadAttachmentDeliveryContent
              selectedItem={selectedItem}
              setModalVisible={setDeliveryAttachmentModalVisible}
            />
          )}
        />
      );
    },
  );

interface Props {
  childern: React.ElementType;
  modalVisible?: boolean;
  isMobileDevice?: boolean;
  setModalVisible: (event: any) => void;
}

const { width } = Dimensions.get("window");

// Styles
const createStyles = (colors: any) =>
  StyleSheet.create({
    centeredView: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(15, 23, 42, 0.4)",
    },
    modalView: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      alignItems: "stretch",
      justifyContent: "flex-start",
      shadowColor: "#0F172A",
      shadowOffset: {
        width: 0,
        height: 10,
      },
      shadowOpacity: 0.1,
      shadowRadius: 20,
      elevation: 10,
      overflow: "hidden",
    },
    button: {
      position: "absolute",
      top: 16,
      right: 16,
      zIndex: 999,
      backgroundColor: colors.surfaceSubtle,
      margin: 0,
    },
    container: {
      flex: 1,
      width: "100%",
      backgroundColor: colors.surface,
      borderRadius: 16,
    },
    loaderContainer: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
    },
    contentContainer: {
      padding: 24,
      paddingTop: 32,
    },
    header: {
      marginBottom: 20,
      borderBottomWidth: 1,
      borderBottomColor: colors.surfaceSubtle,
      paddingBottom: 16,
    },
    headerTitle: {
      fontSize: 22,
      fontWeight: "700",
      color: colors.text,
    },
    headerSubtitle: {
      fontSize: 13,
      color: colors.textSecondary,
      marginTop: 4,
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 12,
    },
    filesListContainer: {
      marginBottom: 12,
    },
    uploadZone: {
      borderWidth: 1,
      borderColor: colors.primary,
      borderStyle: "dashed",
      backgroundColor: colors.primaryBg,
      borderRadius: 8,
      paddingVertical: 12,
      paddingHorizontal: 16,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 16,
    },
    uploadZoneTitle: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.primary,
    },
    uploadZoneSubtitle: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: "center",
      marginTop: 4,
    },
    previewCard: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 16,
      marginBottom: 12,
    },
    previewContent: {
      flexDirection: "row",
      alignItems: "center",
    },
    thumbnail: {
      width: 48,
      height: 48,
      borderRadius: 6,
    },
    docIconContainer: {
      width: 48,
      height: 48,
      borderRadius: 6,
      backgroundColor: colors.primaryBg,
      alignItems: "center",
      justifyContent: "center",
    },
    fileDetails: {
      flex: 1,
      marginLeft: 12,
    },
    fileName: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
    },
    fileType: {
      fontSize: 11,
      color: colors.textSecondary,
      fontWeight: "600",
      marginTop: 2,
    },
    cardActions: {
      flexDirection: "row",
      marginTop: 14,
      borderTopWidth: 1,
      borderTopColor: colors.surfaceSubtle,
      paddingTop: 12,
    },
    cardBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
      flex: 1,
    },
    viewBtn: {
      backgroundColor: colors.primaryBg,
      marginRight: 8,
    },
    viewBtnText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.primary,
    },
    removeBtn: {
      backgroundColor: colors.dangerBg,
      marginLeft: 8,
    },
    removeBtnText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.danger,
    },
    remarksLabel: {
      fontSize: 14,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 8,
      marginTop: 8,
    },
    textArea: {
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      fontSize: 14,
      color: colors.text,
      minHeight: 100,
      textAlignVertical: "top",
      marginBottom: 24,
    },
    submitButton: {
      backgroundColor: colors.primary,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
      justifyContent: "center",
    },
    submitButtonDisabled: {
      opacity: 0.6,
    },
    submitText: {
      fontSize: 15,
      fontWeight: "700",
      color: colors.surface,
    },
    additionalUploadZone: {
      borderColor: colors.primary,
      backgroundColor: colors.primaryBg,
    },
    additionalUploadZoneTitle: {
      color: colors.primary,
    },
    additionalDocIconContainer: {
      backgroundColor: colors.primaryBg,
    },
    additionalViewBtn: {
      backgroundColor: colors.primaryBg,
      marginRight: 8,
    },
    additionalViewBtnText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.primary,
    },
    footer: {
      paddingHorizontal: 24,
      paddingBottom: 24,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: colors.surfaceSubtle,
      backgroundColor: colors.surface,
    },
    previewOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.85)",
      justifyContent: "center",
      alignItems: "center",
    },
    previewImage: {
      width: "90%",
      height: "80%",
    },
    previewClose: {
      position: "absolute",
      top: 40,
      right: 20,
    },
  });
